<script setup lang="ts">
import { withDefaults, defineProps, defineEmits, computed } from "vue";
import { VueFinalModal } from 'vue-final-modal'
import BaseButton from "@/components/ui/BaseButton.vue";
import { useEmitter } from '@nguyenshort/vue3-mitt';
import { ref, onMounted, onUnmounted } from "vue";
import {XMarkIcon} from "@heroicons/vue/24/outline";
const emitter = useEmitter()

const props = withDefaults(defineProps<{
  title?: string,
  subtitle?: string,
  icon?: string,
  fullscreen?: boolean,
  small?: boolean,
  hasConfirm?: boolean,
  confirmLabel?: string,
  hasCancel?: boolean,
  top?: boolean,
  exactWidth?: string,
  scrollable?: boolean,
  blocking?: boolean,
  hasClose?: boolean,
  bodyClass?: string,
}>(), {
  fullscreen: false,
  small: false,
  hasConfirm: false,
  confirmLabel: 'common_done',
  hasCancel: false,
  top: false,
  scrollable: true,
  blocking: false,
  hasClose: true,
  bodyClass: 'px-10 pt-7 pb-6'
})

const emit = defineEmits(['confirm', 'commit', 'update:modelValue'])

const contentClass = computed(() => {
  let defClass = `default-modal-content max-h-[90%] md:max-h-[70%] relative`;

  if (props.fullscreen) {
    defClass += ` default-modal-content--fullscreen`;
  }

  if (props.small) {
    defClass += ` default-modal-content--small`;
  }

  if (props.top) {
    defClass += ` default-modal-content--top`;
  }

  if (props.exactWidth) {
    defClass += ` ${props.exactWidth}`;
  }
  return defClass;
})

const overflow = computed(() => {
  if (props.scrollable) {
    return "auto";
  } else {
    return "hidden";
  }
})

const confirm = () => {
  emit('confirm');
  emit('update:modelValue', false)
}

const secondary = () => {
  emit('update:modelValue', false)
}
const isMobile = ref(window.innerWidth < 800);

const handleResize = () => {
  isMobile.value = window.innerWidth < 800;
};

onMounted(() => {
  window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});

emitter.on('modalClose', () => {
  if (props.blocking) return
  emit('update:modelValue', false)
})

const iconUrl = computed(() => {
    if (props.icon) {
      return require('@/assets/icons/sorenson/' + props.icon + '.svg')
    }

    return "";
})

</script>

<template>
  <VueFinalModal
      class="default-modal z-20"
      :content-class="contentClass"
      content-transition="vfm-slide-down"
      overlay-transition="vfm-fade"
      :swipe-to-close="blocking || !isMobile ? undefined : 'down'"
      :esc-to-close="!blocking"
      :click-to-close="!blocking"
      @closed="secondary"
      data-testid="sheet-modal"
  >
    <template #default="{}">
      <button v-if="hasClose" @click="() => { secondary(); }" class="text-white opacity-75 hover:opacity-100 cursor-pointer absolute right-3 top-3 hidden md:block">
        <XMarkIcon class="size-7" />
      </button>
      <header class="default-modal-header px-2 py-2 flex justify-center items-center flex-col" :class="{'md:hidden': !title}" data-testid="sheet-modal-header">
        <div v-if="hasClose" class="ui-close-indicator md:hidden" data-testid="sheet-modal-close-indicator"></div>
        <div v-if="icon" class="mt-5">
          <img :src="iconUrl" alt="">
        </div>
        <h2 v-if="title" class="mt-6 self-stretch text-center justify-start text-white text-3xl font-bold px-[10%]">{{ title }}</h2>
        <span v-if="subtitle" class="mt-2 text-text-neutral-high text-base px-[10%] text-center">{{ subtitle }}</span>
      </header>
      <div
        class="default-modal-slot gap-6 flex flex-col gap-7"
        :class="[
          bodyClass,
          `overflow-${overflow}`,
          scrollable ? 'scrollbar-thumb-rounded-full scrollbar-thin scrollbar-track-rounded-full scrollbar scrollbar-thumb-[#f0f6ff57] scrollbar-track-transparent overflow-auto' : '',
        ]"
      >
        <slot />
        <footer class="modal-footer" v-if="hasCancel || hasConfirm">
          <slot name="footer" />
          <div class="action-buttons w-full gap-3 flex-col flex">
            <base-button v-if="hasConfirm"
                         level="primary"
                         full-width
                         size="medium" 
                         @click="() => { confirm(); }"
                         data-testid="sheet-modal-confirm-button">{{ $t(confirmLabel) || 'Done' }}</base-button>
            <base-button v-if="hasCancel"
                         level="secondary"
                         full-width
                         size="medium" 
                         @click="() => secondary()"
                         data-testid="sheet-modal-cancel-button">{{ $t('common_cancel') || 'Cancel' }}</base-button>
          </div>
        </footer>
      </div>
    </template>
  </VueFinalModal>
</template>

<style lang="scss">
  .default-modal {
    display: flex;
    justify-content: center;
    align-items: center;

    .dp__menu {
      box-shadow: none;
    }

    &-close {
      margin-left: auto;
    }
  }
  .default-modal-content {
    display: flex;
    flex-direction: column;
    background: var(--layer-2, #171D24);
    border-radius: 20px;
    border: 1px solid rgba(108, 191, 255, 0.13);
    box-shadow: 0px 20px 25px -5px rgba(108, 191, 255, 0.10), 0px 10px 10px -5px rgba(108, 191, 255, 0.00);

    &--fullscreen {
      width: 100%;
    }

    &--small {
      width: 50%;
    }

    &--top {
      top: 10%;
    }

    @media all and (max-width: 800px) {
      position: absolute;
      bottom: 0;
      width: 100%;
    }
  }

  .default-modal-header {
    align-items: center;

    @media all and (max-width: 800px) {
      justify-content: center;
      padding: 8px 0;
      flex-direction: column;
    }
  }

  .modal-footer {
    display: flex;
  }

  .ui-close-indicator {
    width: var(--container-fix-height-48, 48px);
    height: 4px;
    border-radius: 2px;
    background: var(--elements-neutral-34, rgba(240, 246, 255, 0.34));
  }
</style>